import { z } from "zod";
import {
  DIMENSION_RESULTS,
  READINESS_DIMENSIONS,
  accessStateSchema,
  actionAvailabilitySchema,
  contextStatusSchema,
  idSchema,
  instantSchema,
  moneySchema,
  readinessSchema,
  sourceDateSchema,
} from "@amber/contracts";
import { carryNoteSchema, copyViewSchema, matchProposalViewSchema } from "./copies.js";
import { checkOptionsSchema, turnCheckSchema } from "./checks.js";
import {
  dateConflictViewSchema,
  itemKindSchema,
  mediaTypeSchema,
  platformRefSchema,
  stageBarSchema,
  stageDetailSchema,
  statusViewSchema,
} from "@amber/collection/contracts";
import { textDocumentSchema, textLocatorSchema } from "@amber/transcription/contracts";

export const EVIDENCE_RECORD_ACTIONS = {
  translate: "evidence.translate",
  restrict: "collection.delete_item",
  restore: "collection.restore_item",
} as const;

export const DIMENSION_LABELS: Record<(typeof READINESS_DIMENSIONS)[number], string> = {
  original: "Original",
  provenance: "Provenance",
  language: "Language",
  context: "Context",
  text: "Text",
  attribution: "Attribution",
  problems: "Problems",
  access: "Access",
};

export const READINESS_LABELS = { draft: "Draft", eligible: "Eligible" } as const;

export const recordQuerySchema = z.strictObject({
  version: z.coerce.number().int().positive().optional(),
  communication: z.coerce.number().int().positive().optional(),
});

const confidenceSchema = z.strictObject({
  value: z.number().min(0).max(1).nullable(),
  label: z.string(),
  method: z.string().nullable(),
  calibration: z.string(),
  reason: z.string().nullable(),
});
export type ConfidenceView = z.infer<typeof confidenceSchema>;

export const personReadinessSchema = z.strictObject({
  profile_id: idSchema("prf").nullable(),
  name: z.string().nullable(),
  result: readinessSchema,
  reason: z.string().nullable(),
  dimensions: z.array(
    z.strictObject({
      dimension: z.enum(READINESS_DIMENSIONS),
      label: z.string(),
      result: z.enum(DIMENSION_RESULTS),
      reason: z.string().nullable(),
    }),
  ),
});
export type PersonReadiness = z.infer<typeof personReadinessSchema>;

export const communicationViewSchema = z.strictObject({
  id: idSchema("com"),
  ordinal: z.number().int().positive(),
  title: z.string().nullable(),
  event_kind: z.string(),
  range_ms: z.tuple([z.number().int(), z.number().int()]).nullable(),
  context: z.strictObject({ status: contextStatusSchema, label: z.string(), detail: z.string() }),
  readiness: z
    .strictObject({
      result: readinessSchema,
      reason: z.string().nullable(),
      people: z.array(personReadinessSchema),
      assessed_at: instantSchema,
    })
    .nullable(),
  version: z
    .strictObject({
      revision_id: idSchema("rev"),
      version: z.number().int().positive(),
      latest: z.number().int().positive(),
      published_at: instantSchema,
    })
    .nullable(),
  copies: z.strictObject({
    role: z.enum(["primary", "variant"]),
    others: z.number().int().nonnegative(),
    list: z.array(copyViewSchema),
    primary_title: z.string().nullable(),
    proposals: z.array(matchProposalViewSchema),
  }),
  occurrence: z.strictObject({ date: z.string().nullable(), basis: z.string().nullable() }),
  split_from: z.number().int().positive().nullable(),
  timestamps: z.strictObject({
    discovered_at: instantSchema,
    archived_at: instantSchema.nullable(),
    processed_at: instantSchema.nullable(),
    first_eligible_at: instantSchema.nullable(),
    source_available_at: instantSchema.nullable(),
  }),
});
export type CommunicationView = z.infer<typeof communicationViewSchema>;

export const SPEAKER_KINDS = ["tracked", "not_tracked", "no_profile", "ambiguous", "unresolved"] as const;

export const speakerViewSchema = z.strictObject({
  id: idSchema("spk"),
  label: z.string(),
  display: z.string(),
  name: z.string().nullable(),
  role: z.strictObject({ value: z.string(), inferred: z.boolean() }).nullable(),
  kind: z.enum(SPEAKER_KINDS),
  kind_label: z.string(),
  profile: z.strictObject({ id: idSchema("prf"), name: z.string() }).nullable(),
  capacity: z
    .strictObject({
      date: z.string().nullable(),
      role: z.string().nullable(),
      affiliation: z.string().nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  naming: z.strictObject({
    status: z.string(),
    confidence: confidenceSchema.nullable(),
    evidence: z.array(z.strictObject({ ref: z.string(), text: z.string(), at_ms: z.number().int().nullable() })),
    alternatives: z.array(z.strictObject({ name: z.string(), confidence: z.number() })),
    reason: z.string().nullable(),
  }),
  profile_match: z.strictObject({
    decision: z.string(),
    confidence: confidenceSchema.nullable(),
    candidates: z.array(z.strictObject({ id: idSchema("prf"), name: z.string() })),
    reason: z.string().nullable(),
  }),
  lowest_attribution: confidenceSchema,
  speaking_ms: z.number().int().nonnegative(),
  turn_count: z.number().int().nonnegative(),
  lane: z.number().int().nonnegative(),
});
export type SpeakerView = z.infer<typeof speakerViewSchema>;

export const wordViewSchema = z.strictObject({
  i: z.number().int().nonnegative(),
  text: z.string(),
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().nonnegative(),
  confidence: z.number().min(0).max(1).nullable(),
  low: z.boolean(),
  corrected: z
    .strictObject({
      was: z.string().nullable(),
      by: z.string(),
      at: instantSchema,
      reason: z.string(),
      from: z.string().nullable(),
    })
    .nullable(),
});

export const problemViewSchema = z.strictObject({
  id: idSchema("rpt"),
  kind: z.enum(["wrong_text", "wrong_speaker", "wrong_timing", "context", "other"]),
  kind_label: z.string(),
  description: z.string(),
  scope: z.enum(["item", "communication", "passage"]),
  communication: z.number().int().positive(),
  unit_ids: z.array(idSchema("unt")),
  state: z.enum(["open", "resolved"]),
  reported_by: z.string(),
  reported_at: instantSchema,
  resolution: z
    .strictObject({
      kind: z.enum(["correction", "note"]),
      by: z.string(),
      at: instantSchema,
      note: z.string().nullable(),
    })
    .nullable(),
  task_href: z.string().nullable(),
});
export type ProblemView = z.infer<typeof problemViewSchema>;

export const turnViewSchema = z.strictObject({
  idx: z.number().int().nonnegative(),
  n: z.number().int().positive(),
  unit_id: idSchema("unt").nullable(),
  communication: z.number().int().positive().nullable(),
  speaker_id: idSchema("spk").nullable(),
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().nonnegative(),
  language: z.string().nullable(),
  overlap: z.boolean(),
  attribution: confidenceSchema.extend({ uncertain: z.boolean() }),
  passage: z.strictObject({
    confidence: z.number().min(0).max(1).nullable(),
    mark: z.enum(["none", "low_confidence", "flagged"]),
    reasons: z.array(z.string()),
  }),
  words: z.array(wordViewSchema),
  corrected: z
    .strictObject({
      by: z.string(),
      at: instantSchema,
      reason: z.string(),
      kinds: z.array(z.string()),
      from: z.string().nullable(),
    })
    .nullable(),
  confirmed: z.strictObject({ by: z.string(), at: instantSchema }).nullable(),
  problems: z.array(idSchema("rpt")),
  carry_notes: z.array(carryNoteSchema),
  mention: z.string().nullable(),
  locator: textLocatorSchema.nullable(),
  check: turnCheckSchema.nullable(),
});
export type TurnView = z.infer<typeof turnViewSchema>;

export const gapViewSchema = z.strictObject({
  start_ms: z.number().int(),
  end_ms: z.number().int(),
  kind: z.enum(["silence", "not_processed"]),
  label: z.string(),
  hallucination_flagged: z.boolean(),
});

export const translationViewSchema = z.strictObject({
  language: z.string(),
  from: z.array(z.string()),
  label: z.string(),
  model: z.string(),
  segments: z.record(z.string(), z.string()),
});
export type TranslationView = z.infer<typeof translationViewSchema>;

export const historyEntrySchema = z.strictObject({
  at: instantSchema,
  kind: z.enum([
    "publication",
    "reprocessing",
    "capture",
    "access",
    "manual",
    "policy",
    "correction",
    "problem",
    "copy",
    "carry_over",
    "timing",
    "details",
    "check",
  ]),
  label: z.string(),
  by: z.string().nullable(),
  detail: z.string().nullable(),
  version: z.number().int().positive().nullable(),
  revert: idSchema("cov").nullable(),
  reverse: idSchema("spl").nullable(),
});

export const attemptViewSchema = z.strictObject({
  id: idSchema("att"),
  stage: z.string(),
  stage_name: z.string(),
  number: z.number().int().positive(),
  of: z.number().int().positive(),
  kind: z.string(),
  route: z.string().nullable(),
  state: z.string(),
  result: z.string(),
  detail: z.string().nullable(),
  started_at: instantSchema,
  finished_at: instantSchema.nullable(),
  duration_ms: z.number().int().nonnegative().nullable(),
  cost: moneySchema.nullable(),
  cost_estimated: z.boolean(),
  engine_href: z.string().nullable(),
  next: z.strictObject({ text: z.string(), at: instantSchema.nullable() }),
});
export type AttemptView = z.infer<typeof attemptViewSchema>;

export const evidenceRecordSchema = z.strictObject({
  item: z.strictObject({
    id: idSchema("itm"),
    title: z.string(),
    platform: platformRefSchema,
    channel: z.string().nullable(),
    kind: itemKindSchema,
    type_label: z.string(),
    media_type: mediaTypeSchema,
    language: z.string().nullable(),
    duration_ms: z.number().int().nullable(),
    size_bytes: z.number().int().nullable(),
    dates: z.strictObject({
      event: sourceDateSchema.nullable(),
      published: sourceDateSchema.nullable(),
      discovered_at: instantSchema,
      conflicts: z.array(dateConflictViewSchema),
    }),
    access_state: accessStateSchema,
  }),
  status: statusViewSchema,
  stages: z.array(stageBarSchema).length(6),
  readiness: z
    .strictObject({
      result: readinessSchema,
      reason: z.string().nullable(),
      policy: z.strictObject({
        id: idSchema("pol"),
        name: z.string(),
        version: z.number().int().positive(),
        provisional: z.boolean(),
        applied_at: instantSchema,
      }),
      assessed_at: instantSchema,
      eligible_communications: z.number().int().nonnegative(),
    })
    .nullable(),
  failure: z
    .strictObject({
      stage: z.string(),
      stage_name: z.string(),
      title: z.string(),
      reason: z.string().nullable(),
      next: z.string().nullable(),
      attempts: z.array(
        z.strictObject({
          number: z.number().int(),
          of: z.number().int(),
          kind: z.string(),
          at: instantSchema,
          outcome: z.string().nullable(),
          detail: z.string().nullable(),
          route: z.string().nullable(),
          engine_href: z.string().nullable(),
        }),
      ),
      kept: z.string().nullable(),
    })
    .nullable(),
  provenance: z.strictObject({
    original: z.strictObject({
      url: z.string(),
      host: z.string(),
      state: z.string(),
      checked_at: instantSchema.nullable(),
    }),
    archived: z
      .strictObject({
        captured_at: instantSchema,
        size_bytes: z.number().int().nullable(),
        version: z.number().int(),
        versions: z.number().int(),
      })
      .nullable(),
  }),
  communications: z.array(communicationViewSchema),
  selected_communication: z.number().int().positive().nullable(),
  pinned: z
    .strictObject({
      version: z.number().int().positive(),
      latest: z.number().int().positive(),
      published_at: instantSchema,
    })
    .nullable(),
  media: z
    .strictObject({
      href: z.string(),
      mime_type: z.string(),
      kind: z.enum(["video", "audio"]),
      duration_ms: z.number().int().nullable(),
    })
    .nullable(),
  transcript: z
    .strictObject({
      label: z.string(),
      languages: z.array(z.string()),
      generated: z.boolean(),
      word_count: z.number().int(),
      partial_until_ms: z.number().int().nullable(),
    })
    .nullable(),
  document: textDocumentSchema
    .extend({
      address: z.string(),
      captured_at: instantSchema.nullable(),
      artifact_href: z.string().nullable(),
      mime_type: z.string().nullable(),
      replying_to: z
        .strictObject({ label: z.string(), url: z.string(), text: z.string().nullable(), note: z.string().nullable() })
        .nullable(),
    })
    .strict()
    .nullable(),
  speakers: z.array(speakerViewSchema),
  speakers_state: z.enum(["identified", "failed", "pending", "not_applicable"]),
  turns: z.array(turnViewSchema),
  gaps: z.array(gapViewSchema),
  key_facts: z.strictObject({
    clip: z.strictObject({
      duration_ms: z.number().int().nullable(),
      resolution: z.string().nullable(),
      size_bytes: z.number().int().nullable(),
    }),
    speakers: z.strictObject({ detected: z.number().int(), named: z.number().int(), unresolved: z.number().int() }),
    transcript: z.strictObject({ generated: z.boolean(), language: z.string().nullable(), turns: z.number().int() }),
    checks: z.strictObject({ verified: z.number().int(), failed: z.number().int(), rechecking: z.number().int() }),
    translation: z.string(),
    context: z.string(),
  }),
  source: z.strictObject({
    original_url: z.string(),
    canonical_url: z.string(),
    retrieval_url: z.string().nullable(),
    availability: z.array(
      z.strictObject({ at: instantSchema, state: z.string(), label: z.string(), version: z.number().int() }),
    ),
    files: z.array(
      z.strictObject({
        id: idSchema("art"),
        kind: z.string(),
        label: z.string(),
        detail: z.string(),
        size_bytes: z.number().int(),
        href: z.string().nullable(),
        notice: z.string().nullable(),
        version: z.number().int(),
        stored_at: instantSchema,
      }),
    ),
    rights: z
      .strictObject({
        declared_terms: z.string().nullable(),
        holder: z.string().nullable(),
        permission: z.string(),
        restrictions: z.array(z.string()),
        unknowns: z.array(z.string()),
        observed_at: instantSchema,
      })
      .nullable(),
    attempts: z.array(attemptViewSchema),
  }),
  processing: z.strictObject({
    stages: z.array(
      stageDetailSchema.extend({ duration_ms: z.number().int().nullable(), engine_href: z.string().nullable() }),
    ),
    total_cost: moneySchema,
  }),
  history: z.array(historyEntrySchema),
  problems: z.array(problemViewSchema),
  restriction: z
    .strictObject({
      state: z.enum(["restricted", "deleted"]),
      at: instantSchema.nullable(),
      by: z.string().nullable(),
      reason: z.string().nullable(),
    })
    .nullable(),
  actions: z.strictObject({
    restrict: actionAvailabilitySchema,
    restore: actionAvailabilitySchema,
    translate: actionAvailabilitySchema,
    reprocess: actionAvailabilitySchema,
    correct: actionAvailabilitySchema,
    report: actionAvailabilitySchema,
    copies: actionAvailabilitySchema,
    timing: actionAvailabilitySchema,
    check: actionAvailabilitySchema,
  }),
  check_options: checkOptionsSchema,
});
export type EvidenceRecord = z.infer<typeof evidenceRecordSchema>;

export const translationRequestSchema = z.strictObject({ language: z.enum(["en", "de"]) });
export const translationQuerySchema = z.strictObject({
  language: z.enum(["en", "de"]),
  version: z.coerce.number().int().positive().optional(),
});
export const translationResponseSchema = z.discriminatedUnion("state", [
  z.strictObject({ state: z.literal("ready"), translation: translationViewSchema }),
  z.strictObject({ state: z.literal("not_needed"), message: z.string() }),
  z.strictObject({ state: z.literal("waiting"), message: z.string() }),
]);
export type TranslationResponse = z.infer<typeof translationResponseSchema>;
